import { html, nothing } from "lit";
import { icons } from "../icons.ts";
import { renderMarkdownContent } from "../markdown-render.ts";
import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import type { AssistantTurn } from "./chat-assistant-turn.ts";
import { renderChatFileChangesSummary } from "./chat-file-changes-summary.ts";
import { formatChatWaitElapsed } from "./chat-wait-elapsed.ts";

export function findLastAssistantGroupKey(
  items: Array<ChatItem | MessageGroup | AssistantTurn>,
): string | null {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (!item || !("kind" in item)) {
      continue;
    }
    if (item.kind === "assistant-turn") {
      return item.key;
    }
    if (item.kind === "group") {
      const role = item.role?.toLowerCase() ?? "";
      if (role === "assistant") {
        return item.key;
      }
    }
  }
  return null;
}

export function renderMinimalStatusRow(
  label: string,
  startedAt: number,
  waitNowMs?: number,
): ReturnType<typeof html> {
  const waitElapsed = formatChatWaitElapsed(startedAt, waitNowMs ?? Date.now());
  return html`
    <div class="chat-group assistant">
      <div class="chat-group-messages">
        <div class="chat-bubble chat-minimal-status fade-in" role="status" aria-live="polite">
          <span class="chat-minimal-status__spinner" aria-hidden="true">${icons.loader}</span>
          <span class="chat-minimal-status__label">${label}</span>
          <span class="chat-wait-elapsed" aria-label="Waiting time">${waitElapsed}</span>
        </div>
      </div>
    </div>
  `;
}

/**
 * The plan's closing synthesis.
 *
 * This is the one message that answers "what did the run actually produce",
 * so it stays expanded — but it used to render as an undifferentiated
 * assistant bubble indistinguishable from ordinary prose, which made the
 * operator hunt for it in a thread full of child reports. It now carries an
 * explicit completion header (icon + headline) and a bounded, scrollable
 * body, matching the operator-milestone bubble's treatment so the fleet
 * theme styles both.
 */
export function renderFleetCompleteNotice(item: Extract<ChatItem, { kind: "fleet-complete" }>) {
  const state = item.state;
  const body = item.body || item.headline;

  // Without a parsed state (older persisted item, or a body shape this build
  // does not recognise) fall back to the previous raw-markdown body so the
  // operator never sees an empty bubble.
  const hasStructured =
    !!state &&
    (!!state.done ||
      !!state.failed ||
      !!state.cancelled ||
      !!state.goal ||
      (state.answers?.length ?? 0) > 0 ||
      state.findings.length > 0 ||
      state.artifacts.length > 0 ||
      state.failedTitles.length > 0 ||
      state.cancelledTitles.length > 0 ||
      state.notes.length > 0);

  const chips: Array<{ value: string; label: string; tone: string }> = [];
  if (state?.done !== undefined)
    chips.push({ value: String(state.done), label: "done", tone: "ok" });
  if (state?.failed) chips.push({ value: String(state.failed), label: "failed", tone: "bad" });
  if (state?.cancelled)
    chips.push({ value: String(state.cancelled), label: "cancelled", tone: "warn" });
  if (state?.merged === "merged")
    chips.push({ value: "merged", label: "into develop", tone: "ok" });
  else if (state?.merged === "conflict")
    chips.push({ value: "conflict", label: "needs resolve", tone: "bad" });

  return html`
    <div class="chat-group assistant" data-testid="fleet-complete-notice">
      <div class="chat-group-messages">
        <div class="chat-bubble chat-bubble--fleet-complete fade-in" role="status">
          <div class="chat-fleet-complete__head">
            <span class="chat-fleet-complete__icon" aria-hidden="true">✓</span>
            <span class="chat-fleet-complete__title" data-testid="fleet-complete__headline">
              ${item.headline}
            </span>
          </div>
          ${
            chips.length
              ? html`<div class="chat-plan-approval__stats" data-testid="fleet-complete__stats">
                  ${chips.map(
                    (c) => html`<span
                      class="chat-plan-approval__stat chat-plan-approval__stat--${c.tone}"
                      >${c.value}
                      <span class="chat-plan-approval__stat-label">${c.label}</span></span
                    >`,
                  )}
                </div>`
              : nothing
          }
          ${
            hasStructured && state?.goal
              ? html`<div class="chat-fleet-complete__goal" data-testid="fleet-complete__goal">
                  ${state.goal}
                </div>`
              : nothing
          }
          <div class="chat-text chat-fleet-complete__body" data-testid="fleet-complete__body">
            ${
              hasStructured
                ? html`${renderFleetCompletionSections(state)}`
                : renderMarkdownContent(body)
            }
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Render the parsed sections of a completion bubble.
 *
 * Each section is labelled so the operator can tell "what did the units
 * find" apart from "where are the artifacts" without re-reading the whole
 * blob. Findings and artifacts are rendered as lists rather than as
 * markdown paragraphs — the old shape wrapped long paths in a monospace
 * run-on that was hard to scan.
 */
function renderFleetCompletionSections(
  state: NonNullable<Extract<ChatItem, { kind: "fleet-complete" }>["state"]>,
) {
  const section = (label: string, testid: string, items: string[]) =>
    items.length
      ? html`<div class="chat-fleet-complete__section" data-testid="${testid}">
          <div class="chat-fleet-complete__section-label">${label}</div>
          <ul class="chat-fleet-complete__list">
            ${items.map((it) => html`<li>${it}</li>`)}
          </ul>
        </div>`
      : nothing;

  return html`${[
    state.answers?.length
      ? html`<div class="chat-fleet-complete__section" data-testid="fleet-complete__answer">
          <div class="chat-fleet-complete__section-label">Answer</div>
          <div class="chat-fleet-complete__answer">
            ${state.answers.map((answer) => html`<p>${answer}</p>`)}
          </div>
        </div>`
      : nothing,
    state.failedTitles.length
      ? section("Failed units", "fleet-complete__failed", state.failedTitles)
      : nothing,
    state.cancelledTitles.length
      ? section("Cancelled units", "fleet-complete__cancelled", state.cancelledTitles)
      : nothing,
    state.findings.length
      ? section("What the units found", "fleet-complete__findings", state.findings)
      : nothing,
    state.artifacts.length
      ? section("Artifacts", "fleet-complete__artifacts", state.artifacts)
      : nothing,
    state.notes.length ? section("Notes", "fleet-complete__notes", state.notes) : nothing,
  ]}`;
}

/**
 * The plan approval — the moment a plan was approved and its first batch
 * started.
 *
 * The gateway writes the ack as three lines of prose, which used to land
 * in the generic assistant bubble: monospace, same shape as every other
 * assistant turn, so the operator had to read it line by line to learn
 * the only two things that mattered — which plan, and how much of it is
 * running. Lifting the counts into chips and the plan id into a badge
 * makes the approval scannable, and it costs the operator the raw prose
 * only in the sense that the prose said the same things more slowly.
 */
export function renderPlanApprovalNotice(item: Extract<ChatItem, { kind: "plan-approval" }>) {
  const stats: Array<{ value: string; label: string }> = [];
  if (item.runnableNow !== null) {
    stats.push({ value: String(item.runnableNow), label: "runnable now" });
  }
  if (item.unitCount !== null) {
    stats.push({ value: String(item.unitCount), label: item.unitCount === 1 ? "unit" : "units" });
  }
  if (item.dispatched !== null) {
    stats.push({ value: String(item.dispatched), label: "dispatched" });
  }
  return html`
    <div class="chat-group assistant" data-testid="plan-approval-notice">
      <div class="chat-group-messages">
        <div
          class="chat-bubble chat-bubble--plan-approval fade-in"
          role="status"
          aria-live="polite"
        >
          <div class="chat-plan-approval__head">
            <span class="chat-plan-approval__icon" aria-hidden="true">
              ${planApprovalGlyph(item.headline)}
            </span>
            <span class="chat-plan-approval__title" data-testid="plan-approval__headline">
              ${item.headline}
            </span>
            ${
              item.serial
                ? html`<span class="chat-plan-approval__mode" data-testid="plan-approval__mode">
                    serial
                  </span>`
                : nothing
            }
            ${
              item.planId
                ? html`<span class="chat-plan-approval__id" data-testid="plan-approval__plan-id">
                    ${item.planId}
                  </span>`
                : nothing
            }
          </div>
          ${
            stats.length > 0
              ? html`
                  <div class="chat-plan-approval__stats" data-testid="plan-approval__stats">
                    ${stats.map(
                      (stat) => html`
                        <span class="chat-plan-approval__stat">
                          <span class="chat-plan-approval__stat-value">${stat.value}</span>
                          <span>${stat.label}</span>
                        </span>
                      `,
                    )}
                  </div>
                `
              : nothing
          }
          ${
            item.hint
              ? html`<div class="chat-plan-approval__hint" data-testid="plan-approval__hint">
                  ${item.hint}
                </div>`
              : nothing
          }
        </div>
      </div>
    </div>
  `;
}

/** One glyph per ack shape, so the header reads before the words do. */
function planApprovalGlyph(headline: string): string {
  if (headline.startsWith("Work plan proposed")) {
    return "◌";
  }
  if (headline.startsWith("Work plan started")) {
    return "▶";
  }
  return "✓";
}

/**
 * A collapsed section holding consecutive subagent results.
 *
 * Collapsed by default and titled with a count, so the parent thread stays
 * scannable while the detail remains one click away. Each child result keeps
 * its own `<details>` so the operator can read exactly the one subagent they
 * care about instead of expanding the whole run.
 */
export function renderSubagentResultsSection(
  item: Extract<ChatItem, { kind: "subagent-results" }>,
  opts: { isExpanded: boolean; onToggle: () => void } = {
    isExpanded: false,
    onToggle: () => undefined,
  },
) {
  const count = item.results.length;
  const countLabel = `${count} result${count === 1 ? "" : "s"}`;
  // A one-item group is a single report, not a section. Rendering it inside
  // the outer `<details>` would nest one disclosure inside another and make
  // the operator click twice for the thing they came to read.
  const solo = count === 1 ? item.results[0] : undefined;
  const onItemToggle = (e: Event) => {
    const el = e.currentTarget as HTMLDetailsElement;
    if (el.open !== opts.isExpanded) opts.onToggle();
  };
  if (solo) {
    return html`
      <div
        class="chat-group assistant chat-subagent-results chat-subagent-results--solo"
        data-testid="subagent-results"
        data-count=${count}
        data-expanded=${opts.isExpanded ? "true" : "false"}
      >
        <div class="chat-group-messages">
          <details
            class="chat-subagent-results__item chat-subagent-results__item--solo"
            data-testid="subagent-results__item"
            data-result-key=${solo.key}
            ?open=${opts.isExpanded}
            @toggle=${onItemToggle}
          >
            <summary class="chat-subagent-results__item-summary">
              <span class="chat-subagent-results__chevron" aria-hidden="true">▶</span>
              <span class="chat-subagent-results__solo-label">Subagent report</span>
              <span
                class="chat-subagent-results__count muted"
                data-testid="subagent-results__count"
              >
                ${countLabel}
              </span>
              <span class="chat-subagent-results__item-preview">
                ${summarizeResult(solo.body, solo.label)}
              </span>
            </summary>
            <div class="chat-text">${renderMarkdownContent(solo.body)}</div>
          </details>
        </div>
      </div>
    `;
  }
  return html`
    <div
      class="chat-group assistant chat-subagent-results"
      data-testid="subagent-results"
      data-count=${count}
      data-expanded=${opts.isExpanded ? "true" : "false"}
    >
      <div class="chat-group-messages">
        <details
          class="chat-subagent-results__section"
          ?open=${opts.isExpanded}
          @toggle=${onItemToggle}
        >
          <summary class="chat-subagent-results__summary">
            <span class="chat-subagent-results__chevron" aria-hidden="true">▶</span>
            <span data-testid="subagent-results__label"> Subagent results </span>
            <span class="chat-subagent-results__count muted" data-testid="subagent-results__count">
              ${countLabel}
            </span>
          </summary>
          <div class="chat-subagent-results__list">
            ${item.results.map(
              (result, idx) => html`
                <details
                  class="chat-subagent-results__item"
                  data-testid="subagent-results__item"
                  data-result-key=${result.key}
                >
                  <summary class="chat-subagent-results__item-summary">
                    <span class="chat-subagent-results__item-index muted">${idx + 1}</span>
                    <span class="chat-subagent-results__item-preview">
                      ${summarizeResult(result.body, result.label)}
                    </span>
                  </summary>
                  <div class="chat-text">${renderMarkdownContent(result.body)}</div>
                </details>
              `,
            )}
          </div>
        </details>
      </div>
    </div>
  `;
}

/** Status lines carry no identifying information, so the preview skips them. */
const PREVIEW_BOILERPLATE_RE =
  /^\**\s*(?:status\s*[:—-]?\s*)?(?:done|ok|complete[d]?|finished|success(?:ful)?)\.?\s*\**[:—-]?$/i;

/**
 * The collapsed row's label: the announce's task name when we have one,
 * otherwise the first substantive line of the body, clipped.
 *
 * The task name wins because it is the only text that says *which* unit
 * produced the report; the body's first line is frequently boilerplate
 * (`**DONE**`) or a long evidence path.
 */
function summarizeResult(body: string, label?: string): string {
  if (label?.trim()) {
    const task = label.trim();
    return task.length > 140 ? `${task.slice(0, 140)}…` : task;
  }
  const line = body
    .split(/\r?\n/)
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !PREVIEW_BOILERPLATE_RE.test(l));
  if (!line) return "No output";
  return line.length > 140 ? `${line.slice(0, 140)}…` : line;
}

export function renderFleetResultNotice(item: Extract<ChatItem, { kind: "fleet-result" }>) {
  return html`
    <div class="chat-group assistant" data-testid="fleet-result-notice">
      <div class="chat-group-messages">
        <div class="chat-bubble fade-in">
          <div class="chat-text">${renderMarkdownContent(item.body)}</div>
        </div>
      </div>
    </div>
  `;
}

export function renderOperatorMilestoneNotice(
  item: Extract<ChatItem, { kind: "operator-milestone" }>,
) {
  return html`
    <div class="chat-group assistant" data-testid="operator-milestone-notice">
      <div class="chat-group-messages">
        <div class="chat-bubble chat-bubble--operator-milestone fade-in" role="status">
          <span class="chat-operator-milestone__label">Progress</span>
          <div class="chat-text">${renderMarkdownContent(item.body)}</div>
        </div>
      </div>
    </div>
  `;
}

export function renderFilesChangedTabRow(
  item: Extract<ChatItem, { kind: "file-changes" }>,
  opts?: {
    onFileClick?: (changeId: string, path: string, artifactSessionKey?: string) => void;
  },
) {
  const files = item.payload.summary?.totals?.files ?? 0;
  const title = `${files} file${files === 1 ? "" : "s"}`;
  return html`
    <div class="chat-files-tab-row">
      ${renderChatFileChangesSummary(item.payload, {
        defaultOpen: false,
        onFileClick: opts?.onFileClick,
      })}
      <span class="chat-files-tab-chip muted" aria-label="Files changed summary">${title}</span>
    </div>
  `;
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
